import type { HttpMethod, HttpRequestSpec } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { env } from "../env";
import { $selectedEnvironmentName } from "../environments";
import { store } from "../store";
import { invokeExecuteEnvFetch } from "./execute-request";
import { isTauriIpcUrl, shouldBypassProxy } from "./fetch-proxy-routing";
import type { EnvFetchResponse } from "./request-page.types";
import { createSafeResponse } from "./safe-response";

export { isTauriIpcUrl, shouldBypassProxy } from "./fetch-proxy-routing";

const logger = new Logger("general-fetch-interceptor");

type FormFilePart = {
	key: string;
	filename: string;
	contentType: string;
	dataBase64: string;
};

const getDocumentIdFromPath = (): string | null => {
	try {
		const segments = window.location.pathname.split("/").filter(Boolean);
		const documentRouteIndex = segments.findIndex(
			(segment) => segment === "d" || segment === "e"
		);
		if (documentRouteIndex !== -1 && documentRouteIndex + 2 < segments.length) {
			return segments[documentRouteIndex + 2] ?? null;
		}
	} catch {
		// window.location may not be available in some contexts
	}

	return null;
};

// Active execution context set by the request executor before it invokes user
// code. Lets the fetch interceptor resolve workspace/profile for code-cell
// requests where URL parsing can't reliably recover the documentId (e.g.
// sandboxed routes, or Octokit clients constructed in one cell and called
// from another). Reads from this win over `getDocumentIdFromPath()`.
let currentFetchContext: { documentId?: string } | null = null;

/**
 * Set the current fetch execution context. Returns a disposer that restores
 * the previous context — call it in a `finally` block so concurrent executions
 * restore the stack cleanly. For same-doc concurrent runs the documentId is
 * the same either way, so there's no real risk of mixing up contexts.
 */
export const setCurrentFetchContext = (ctx: { documentId?: string }): (() => void) => {
	const prev = currentFetchContext;
	currentFetchContext = { ...ctx };
	return () => {
		currentFetchContext = prev;
	};
};

const resolveDocumentIdForIntercept = (): string | null => {
	return currentFetchContext?.documentId ?? getDocumentIdFromPath();
};

// `isTauriIpcUrl` / `shouldBypassProxy` live in `./fetch-proxy-routing` so
// their tests can import them without also dragging the execute-request
// module graph in. Re-exported at the top of this file for call-site
// ergonomics.

/** Collect headers into a key-value array for the Rust HttpRequestSpec. */
const collectHeadersAsKv = (
	headers: HeadersInit | undefined
): { enabled: boolean; key: string; value: string }[] => {
	const result: { enabled: boolean; key: string; value: string }[] = [];
	if (!headers) {
		return result;
	}

	if (headers instanceof Headers) {
		headers.forEach((value, key) => {
			result.push({ enabled: true, key, value });
		});
	} else if (Array.isArray(headers)) {
		for (const [key, value] of headers) {
			result.push({ enabled: true, key, value });
		}
	} else {
		for (const [key, value] of Object.entries(headers)) {
			result.push({ enabled: true, key, value });
		}
	}

	return result;
};

let installed = false;

const getSelectedEnvironmentNameOrThrow = async (): Promise<string> => {
	try {
		return await store.get($selectedEnvironmentName);
	} catch (error) {
		logger.error("Failed to read selected environment for fetch proxy", { cause: error });
		throw new Error("Fetch proxy error: selected environment is unavailable");
	}
};

const isSecretsProxyRequest = (url: string): boolean => {
	if (!env.secretsProxyUrl) {
		return false;
	}

	try {
		return new URL(url).origin === new URL(env.secretsProxyUrl).origin;
	} catch {
		return false;
	}
};

/**
 * Extract the request body into the shape Rust expects. Handles all the
 * body-init types the Fetch API accepts:
 *  - FormData → `form` entries for string fields + base64 `files` for File/Blob
 *  - URLSearchParams → `form-urlencoded` mode
 *  - string / Uint8Array / ArrayBuffer / Blob / ReadableStream → raw text
 */
const buildBodySpec = async (
	rawBody: BodyInit | null | undefined
): Promise<HttpRequestSpec["body"] & { files?: FormFilePart[] }> => {
	if (rawBody instanceof FormData) {
		const formKvs: { enabled: boolean; key: string; value: string }[] = [];
		const files: FormFilePart[] = [];
		const pending: Promise<void>[] = [];
		rawBody.forEach((value, key) => {
			if (typeof value === "string") {
				formKvs.push({ enabled: true, key, value });
				return;
			}
			const file = value as File;
			pending.push(
				file.arrayBuffer().then((buf) => {
					const bytes = new Uint8Array(buf);
					let binary = "";
					for (let j = 0; j < bytes.length; j++) {
						binary += String.fromCharCode(bytes[j]);
					}
					files.push({
						key,
						filename: file.name || "blob",
						contentType: file.type || "application/octet-stream",
						dataBase64: btoa(binary),
					});
				})
			);
		});
		await Promise.all(pending);
		return { mode: "form", text: "", form: formKvs, files };
	}

	if (typeof URLSearchParams !== "undefined" && rawBody instanceof URLSearchParams) {
		const formKvs: { enabled: boolean; key: string; value: string }[] = [];
		rawBody.forEach((value, key) => formKvs.push({ enabled: true, key, value }));
		return { mode: "form-urlencoded", text: "", form: formKvs };
	}

	let text = "";
	if (rawBody) {
		if (typeof rawBody === "string") {
			text = rawBody;
		} else if (rawBody instanceof Uint8Array) {
			text = new TextDecoder().decode(rawBody);
		} else if (rawBody instanceof ArrayBuffer) {
			text = new TextDecoder().decode(new Uint8Array(rawBody));
		} else if (rawBody instanceof ReadableStream) {
			text = await new Response(rawBody).text();
		} else if (rawBody instanceof Blob) {
			text = await rawBody.text();
		} else {
			text = String(rawBody);
		}
	}
	return { mode: text ? "raw" : "none", text, form: [] };
};

/**
 * Install a global `fetch` override that routes HTTP(S) requests through Rust
 * via `execute_env_fetch`, bypassing browser CORS restrictions and resolving
 * `{{env.X}}` substitutions on the Rust side.
 *
 * Must be called AFTER `ensureAwsFetchInterceptor()` so the chain is:
 *   general interceptor → AWS interceptor → native fetch
 *
 * Bypassed URL categories (see `shouldBypassProxy`): AWS, Tauri IPC, CDN
 * imports, localhost docsapi (port 3333), reboot collab (*.prod1.rbt.cloud).
 *
 * Safe to call multiple times — only installs once. No-op on non-desktop
 * builds because the underlying Tauri command isn't available there.
 */
export const ensureGeneralFetchInterceptor = (): void => {
	if (installed) {
		return;
	}
	if (!env.isDesktopApp) {
		return;
	}
	installed = true;

	const previousFetch = globalThis.fetch.bind(globalThis);

	globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		let url: string;
		if (typeof input === "string") {
			url = input;
		} else if (input instanceof URL) {
			url = input.toString();
		} else if (input instanceof Request) {
			url = input.url;
		} else {
			return previousFetch(input, init);
		}

		if (shouldBypassProxy(url)) {
			return previousFetch(input, init);
		}
		if (isSecretsProxyRequest(url)) {
			return previousFetch(input, init);
		}

		const rawMethod = init?.method ?? (input instanceof Request ? input.method : "GET");
		const method = rawMethod.toUpperCase() as HttpMethod;
		const headers = init?.headers ?? (input instanceof Request ? input.headers : undefined);
		const rawBody = init?.body ?? (input instanceof Request ? input.body : undefined);
		const headerKvs = collectHeadersAsKv(headers);
		const body = await buildBodySpec(rawBody);
		const documentId = resolveDocumentIdForIntercept() ?? undefined;
		const environmentName = await getSelectedEnvironmentNameOrThrow();

		const spec: HttpRequestSpec = {
			type: "http",
			method,
			url,
			params: [],
			vars: [],
			headers: headerKvs,
			auth: { type: "none" },
			body,
			timeoutMs: 30000,
			followRedirects: true,
			validateSSL: true,
		};

		let response: EnvFetchResponse;
		try {
			response = await invokeExecuteEnvFetch(spec, undefined, environmentName, documentId);
		} catch (err) {
			const msg =
				err instanceof Error
					? err.message
					: typeof err === "string"
						? err
						: JSON.stringify(err);
			throw new Error(`Fetch proxy error: ${msg}`);
		}

		let responseBody: BodyInit | null = response.body;
		if (response.body_base64) {
			const binary = atob(response.body_base64);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) {
				bytes[i] = binary.charCodeAt(i);
			}
			responseBody = bytes;
		}
		return createSafeResponse(responseBody, {
			status: response.status,
			statusText: response.status_text,
			headers: new Headers(response.headers),
		});
	};

	logger.debug("General fetch interceptor installed");
};
